import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

export const brandColors = {
  orange: '#FF8200',
  orangeLight: '#F59C12',
  blue: '#006EB7',
  navy: '#024988',
  green: '#00A651',
  white: '#FFFFFF',
  blueLight: '#3AA9DC',
} as const;

export const sensorColors = {
  online: '#00A651',
  offline: '#8c8c8c',
  warning: '#FF8200',
  critical: '#ff4d4f',
  maintenance: '#F59C12',
} as const;

export const triageColors = {
  batteryDrained: '#faad14',
  hardwareFault: '#ff4d4f',
  // U7.1: HW_EOL was orange (#FF8200), identical to TAMPERING — operators
  // couldn't distinguish them at a glance. Per Mark's framework the two are
  // operationally different (TAMPERING = one-shot user removal; HW_EOL =
  // aged-out cohort, time to bulk-replace). Purple = "spent / end of life",
  // distinct from both "broken" (red) and "tampered" (orange).
  hardwareFaultEol: '#722ed1',
  offlineUnknown: '#006EB7',

  priorityP1: '#ff4d4f',
  priorityP2: '#FF8200',
  priorityP4: '#006EB7',

  actionHardware: '#ff4d4f',
  actionTampering: '#FF8200',
  actionHardwareEol: '#722ed1',
  actionFirmware: '#006EB7',
  // OOR was #faad14 (yellow), identical to Battery — operators couldn't
  // distinguish them on the close action bar. Teal reads as "wireless/range"
  // and is distinct from every other class color.
  actionOutOfRange: '#13c2c2',
  actionOnSite: '#8c8c8c',
  actionFalseAlert: '#d9d9d9',
  actionClose: '#00A651',

  batteryDrainedBg: '#faad1419',
  hardwareFaultBg: '#ff4d4f19',
  hardwareFaultEolBg: '#722ed119',
  offlineUnknownBg: '#006EB719',

  batteryWarning: '#faad14',
  batteryCritical: '#ff4d4f',
  batteryOk: '#00A651',

  signalWeak: '#FF8200',
  signalCritical: '#ff4d4f',
  signalOk: '#00A651',
} as const;

export const chartColors = {
  primary: ['#006EB7', '#3AA9DC', '#024988', '#4FB8E8', '#0081CB'],
  semantic: ['#00A651', '#FF8200', '#F59C12', '#ff4d4f', '#52c41a'],
} as const;

const sharedComponents: NonNullable<ThemeConfig['components']> = {
  Button: { borderRadius: 6, controlHeight: 32, fontWeight: 500 },
  Input: { borderRadius: 6, controlHeight: 32 },
  Select: { controlHeight: 32 },
  Card: { borderRadius: 8, paddingLG: 16 },
  Table: {
    borderRadius: 6,
    cellPaddingBlockSM: 6,
    cellPaddingInlineSM: 8,
  },
  List: { itemPaddingSM: '6px 12px' },
  Tag: { borderRadiusSM: 4 },
  Descriptions: { itemPaddingBottom: 8 },
  Segmented: { controlHeight: 28 },
  Menu: { itemHeight: 48 },
};

export const antdTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: brandColors.blue,
    colorSuccess: brandColors.green,
    colorWarning: brandColors.orange,
    colorInfo: brandColors.blueLight,
    colorError: '#ff4d4f',
    colorBgContainer: brandColors.white,
    colorBgLayout: '#f5f5f5',
    colorText: '#1a1a1a',
    colorTextSecondary: '#595959',
    colorTextTertiary: '#8c8c8c',
    colorBorderSecondary: '#f0f0f0',
    colorLink: brandColors.blue,
    colorLinkHover: brandColors.blueLight,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 13,
    borderRadius: 6,
    lineHeight: 1.4,
    controlHeight: 32,
    controlHeightSM: 24,
  },
  components: {
    ...sharedComponents,
    Table: {
      ...sharedComponents.Table,
      headerBg: '#fafafa',
      rowHoverBg: '#f5f8ff',
    },
    Layout: {
      siderBg: '#ffffff',
      headerBg: '#ffffff',
      bodyBg: '#f0f2f5',
      headerHeight: 48,
      headerPadding: '0 16px',
    },
  },
};
